import { useEffect, useState } from 'react';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useLazyCheckClientExistsInInsightQuery } from '@/features/clients/clientsApi';
import { useGetDriveItemQuery, useGetFoldersAndFilesQuery } from '@/features/msGraph/msGraphApi';
import validationSchema, {
	FOLDER_NAME,
} from '@/components/dialogs/AddFolderSharepointDialog/validationSchema';
import { useTabContext } from '@/components/layouts/TabHeader/TabContext';
import { getPrimaryObject } from '@/utils/getPrimaryObject';

/**
 * Validates a new client/deal name: the SharePoint folder name must be valid and unique among
 * its sibling folders, and a client name must not already exist in the owner's brokerage.
 */
export const useNameValidation = () => {
	const {
		clientRow,
		dealRow,
		isClientView: isClient,
		relationshipCompany,
		isRelationshipCompanyView,
	} = useObjectContext();
	const isClaim = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_CLAIMS;
	const { original, titleChanged } = useTabContext();
	const primaryObject = getPrimaryObject({
		isRelationshipCompany: isRelationshipCompanyView,
		isClient,
		relationshipCompany,
		client: clientRow,
		deal: dealRow,
	});

	const [error, setError] = useState('');

	const [checkClientExists] = useLazyCheckClientExistsInInsightQuery();

	const itemId = primaryObject?.driveId;
	const clientFolderDriveId = primaryObject?.clientFolderDriveId;

	const { data: driveItem } = useGetDriveItemQuery(
		{
			driveId: clientFolderDriveId,
			itemId,
		},
		{ skip: !itemId || !clientFolderDriveId },
	);

	const { data: parentFolders } = useGetFoldersAndFilesQuery(
		{
			itemId: driveItem?.parentReference?.id,
			driveId: clientFolderDriveId,
			recursive: false,
		},
		{ skip: !driveItem?.parentReference?.id || !clientFolderDriveId },
	);

	useEffect(
		() => console.log('Parent folders', parentFolders?.folders),
		[parentFolders?.folders],
	);

	const validateClient = async (name) => {
		const res = await checkClientExists({
			companyName: name.trim(),
			// Doc id of the client's owner — searches their brokerage for duplicates.
			userId: clientRow?.ownerDocId,
		}).unwrap();

		const clientExists = res.length > 0;
		setError(clientExists ? 'Client already exists under your brokerage.' : '');
		return !clientExists;
	};

	const validateFolderName = async (name) => {
		const trimmedName = name.trim();

		// Skip validation
		if (original.title === trimmedName) {
			return true;
		}

		const folders = (parentFolders?.folders ?? [])
			.filter((f) => f.id !== driveItem?.id)
			.map((f) => {
				try {
					return decodeURI(f.name.normalize());
				} catch (e) {
					console.log('Error decode URI', e);
					return f.name.normalize();
				}
			});

		console.log('validate folder name', folders);
		try {
			const nameLabel = isRelationshipCompanyView
				? 'Company name'
				: isClient
					? 'Client name'
					: isClaim
						? 'Claim name'
						: 'Deal name';
			const recordType = isRelationshipCompanyView
				? 'relationshipCompany'
				: isClient
					? 'client'
					: isClaim
						? 'claim'
						: 'deal';

			await validationSchema(folders, nameLabel, recordType).validate(
				{ [FOLDER_NAME]: trimmedName },
				{ strict: true },
			);
			setError('');
			return true;
		} catch (err) {
			console.log('Folder validation error', err?.message);
			setError(err?.message);
			return false;
		}
	};

	// Full check before saving; an unchanged name is always valid
	const validate = async (name) => {
		if (!titleChanged) return true;

		const validFolderName = await validateFolderName(name);
		if (!validFolderName || !isClient) return validFolderName;

		return validateClient(name);
	};

	return { error, setError, validateFolderName, validate };
};
